import React, { useContext, useEffect } from "react"
import { useForm } from "react-hook-form"
import { UserContext } from "../../components/HomeApp"
import { CoverPhotoUploadSection } from "../BandEditPage"
import { Youtube } from "./Youtube"
import { Soundcloud } from "./Soundcloud"
import { Bandcamp } from "./Bandcamp"
import { Spotify } from "./Spotify"
import { Instagram } from "./Instagram"
import { Facebook } from "./Facebook"
import { BandName } from "./BandName"
import { EmailAddress } from "./EmailAddress"
import { MailingList } from "./MailingList"
import { Link } from "react-router-dom"
import { BandLocation } from "./BandLocation"
import { BandType } from "./BandType"
import { BandScene } from "./BandScene"
import { WebsiteUrl } from "./WebsiteUrl"
import { BandBio } from "./BandBio"
import { BandGenre } from "./BandGenre"
import { BacklineOfferings } from "./BacklineOfferings"
import { AvailableForBackgroundGigs } from "./AvailableForBackgroundGigs"
import { VenueHours } from "./VenueHours"
import { AgeRequirements } from "./AgeRequirements"
import { Capacity } from "./Capacity"
import { FoodOptions } from "./FoodOptions"
import { DrinkOptions } from "./DrinkOptions"
import { IndoorsOutdoors } from "./IndoorsOutdoors"
import { PetFriendly } from "./PetFriendly"
import { IsLocationSecret } from "./IsLocationSecret"
import { EntranceInstructions } from "./EntranceInstructions"
import { Band } from "../../types/bands"
import { BandEditFormProvider } from "./BandEditFormContext"
import { Template } from "./Template"
import { CoverPhotoPosition } from "./CoverPhotoPosition"

interface BandEditFormProps {
  band: Band
  isOnboarding?: boolean
}

export const BandEditForm: React.FC<BandEditFormProps> = ({
  band,
  isOnboarding = false,
}) => {
  // Initialize react-hook-form with default values from band
  const form = useForm({
    defaultValues: {
      name: band?.name || "",
      band_type: band?.band_type || "band",
      location: band?.location || null,
      genre: band?.genre || "",
      description: band?.description || "",
      bio: band?.bio || "",
      backline_offerings: band?.backline_offerings || "",
      available_for_background_gigs: band?.available_for_background_gigs || "",
      email: band?.email || "",
      mailing_list_url: band?.mailing_list_url || "",
      website_url: band?.website_url || "",
      instagram_url: band?.instagram_url || "",
      facebook_url: band?.facebook_url || "",
      bandcamp_url: band?.bandcamp_url || "",
      soundcloud_url: band?.soundcloud_url || "",
      spotify_url: band?.spotify_url || "",
      youtube_url: band?.youtube_url || "",
      template_name: band?.template_name || null,
      cover_photo_position: band?.cover_photo_position || "center",
      additional_info: {
        hours: band?.additional_info?.hours || "",
        capacity: band?.additional_info?.capacity || "",
        age_requirements: band?.additional_info?.age_requirements || "",
        food_options: band?.additional_info?.food_options || "",
        drink_options: band?.additional_info?.drink_options || "",
        indoors_outdoors: band?.additional_info?.indoors_outdoors || "",
        pet_friendly: band?.additional_info?.pet_friendly || "",
        is_location_secret: band?.additional_info?.is_location_secret || "",
        entrance_instructions: band?.additional_info?.entrance_instructions || "",
      },
    },
    mode: "onChange",
  })

  const { user } = useContext(UserContext)

  // Prefill the band's public email with the signup email on first setup
  // only - this just seeds the form value through the normal field-change
  // path, so it still goes through EditBandFormField's own auto-save rather
  // than needing a separate save call here.
  useEffect(() => {
    if (isOnboarding && !band?.email && user?.email) {
      form.setValue("email", user.email)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Watch band_type from the form to update UI in real-time
  const bandType = form.watch("band_type")

  const friendlyBandUrl = (band: Band): string =>
    `https://${band.custom_subdomain}.bandnada.com`

  const shouldHideGenre = ["venue", "blog", "promoter", "fan"].includes(
    bandType,
  )

  return (
    <BandEditFormProvider form={form} bandId={band.id}>
      {isOnboarding && band.name && (
        <>
          <h2 className="mt-2 font-bold">
            Your bandNada page is{" "}
            <Link to={band.band_path} className="text-bn-neon-red">
              {friendlyBandUrl(band)}
            </Link>
          </h2>
          <p className="mt-2">
            Fill out as many urls below as you can, and we'll sync all your
            bandcamp music, and your profile will include links to
            everything else you provide.
          </p>
        </>
      )}
      <BandName band={band} />

      <BandType band={band} />
      <BandLocation band={band} />
      <BandScene band={band} />

      <CoverPhotoUploadSection band={band} />
      <CoverPhotoPosition band={band} />
      {!shouldHideGenre && <BandGenre band={band} />}
      {!shouldHideGenre && <AvailableForBackgroundGigs band={band} />}
      <BandBio band={band} />
      <EmailAddress band={band} />
      <MailingList band={band} />
      <WebsiteUrl band={band} />
      <Instagram band={band} />
      <Facebook band={band} />
      {bandType === "venue" && (
        <>
          <h2 className="lg:col-span-8 col-span-12 font-bold border-b mb-4 border-black lg:text-2xl text-xl uppercase mt-10">
            Venue Information
          </h2>
          <BacklineOfferings band={band} />
          <VenueHours band={band} />
          <Capacity band={band} />
          <AgeRequirements band={band} />
          <FoodOptions band={band} />
          <DrinkOptions band={band} />
          <IndoorsOutdoors band={band} />
          <PetFriendly band={band} />
          <IsLocationSecret band={band} />
          <EntranceInstructions band={band} />
        </>
      )}
      <h2 className="lg:col-span-8 col-span-12 font-bold border-b mb-4 border-black lg:text-2xl text-xl uppercase mt-10">
        Page Template
      </h2>
      <Template band={band} />
      <h2 className="lg:col-span-8 col-span-12 font-bold border-b mb-4 border-black lg:text-2xl text-xl uppercase mt-10">
        Music
      </h2>
      <Bandcamp band={band} />
      <Soundcloud band={band} />
      <Spotify band={band} />
      <h2 className="lg:col-span-8 col-span-12 font-bold border-b mb-4 border-black lg:text-2xl text-xl uppercase mt-10">
        Videos
      </h2>
      <Youtube band={band} />
    </BandEditFormProvider>
  )
}
